import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { S, t } from '../../i18n/strings';
import { useFinePointer } from '../../lib/motion';
import { readInt, writeString } from '../../lib/storage';
import { useSteve } from '../../state/context';
import styles from './pixel.module.css';
import {
  createGame,
  GAME_HEIGHT,
  GAME_WIDTH,
  NO_INPUT,
  START_LIVES,
  STEVE_LINE,
  stepGame,
  type GameState,
  type Input,
} from './game';
import { BUBBLE, drawSprite, FOE, HOOK, STEVE, SPRITE_PALETTE } from './sprites';
import { ACHIEVEMENTS, readAwards, unlockedBy, writeAwards, type AchievementId } from './achievements';
import { AwardsPanel, AwardToasts, TOAST_MAX, TOAST_MS, type Toast } from './Awards';

/** Meilleur score, gardé d'une visite à l'autre. */
const BEST_KEY = 'steve.pixel.best';
const BEST_MAX = 9_999_999;

/** Une couleur de corps par rangée : la plus haute vaut le plus de points. */
const ROW_PALETTES = [
  { ...SPRITE_PALETTE, O: '#e05f9c', D: '#e05f9c' },
  { ...SPRITE_PALETTE, O: '#35c2c2', D: '#35c2c2' },
  { ...SPRITE_PALETTE, O: '#6ee36e', D: '#6ee36e' },
];

interface Props {
  readonly onQuit: () => void;
}

/**
 * Le mini-jeu, en premier plan par-dessus le site.
 *
 * Toute la logique vit dans `game.ts` ; ce composant ne fait que trois choses :
 * lire les commandes, appeler `stepGame` à cadence fixe, et peindre l'état
 * dans un canvas de 192 × 144 pixels agrandi sans interpolation.
 *
 * Le rendu ne passe pas par React : l'état de la partie vit dans une `ref` et
 * la boucle dessine directement. Seuls le score, les vies et la fin de partie
 * remontent dans l'état React — soit une poignée de rendus par partie au lieu
 * de soixante par seconde.
 */
export function SteveInvaders({ onQuit }: Props): ReactNode {
  const { lang } = useSteve();
  const fine = useFinePointer();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<GameState>(createGame(Date.now() % 100000, 'ready'));
  const inputRef = useRef<Input>({ ...NO_INPUT });
  const keysRef = useRef({ left: false, right: false, fire: false });

  const [hud, setHud] = useState({ score: 0, lives: START_LIVES, wave: 1, phase: 'ready' as const as GameState['phase'] });
  const [best, setBest] = useState(() => readInt(BEST_KEY, 0, BEST_MAX));

  // ── Succès ──────────────────────────────────────────────────────────────
  /*
   * La liste tombée vit dans une `ref` autant que dans l'état React : la boucle
   * de jeu la consulte soixante fois par seconde et ne peut pas attendre le
   * rendu suivant pour savoir qu'un succès vient d'être compté — sans elle, un
   * même succès se notifierait plusieurs fois de suite.
   */
  const wonRef = useRef<Set<AchievementId>>(new Set(readAwards()));
  const [won, setWon] = useState<readonly AchievementId[]>(() => readAwards());
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  /** Le tableau des succès met la partie en pause : on n'y meurt pas de dos. */
  const pausedRef = useRef(false);
  pausedRef.current = panelOpen;
  const timersRef = useRef<number[]>([]);
  const toastKeyRef = useRef(0);

  const start = useCallback((): void => {
    gameRef.current = createGame((Date.now() ^ Math.floor(Math.random() * 1e6)) | 0, 'playing');
    inputRef.current = { ...NO_INPUT };
    setHud({ score: 0, lives: START_LIVES, wave: 1, phase: 'playing' });
  }, []);

  /** Enregistre des succès, les affiche, et programme leur retrait. */
  const celebrate = useCallback((ids: readonly AchievementId[]): void => {
    for (const id of ids) wonRef.current.add(id);
    const list = ACHIEVEMENTS.filter((id) => wonRef.current.has(id));
    writeAwards(list);
    setWon(list);

    for (const id of ids) {
      // Compteur strictement croissant : deux succès tombés dans la même
      // milliseconde auraient partagé une clé s'ils s'étaient datés eux-mêmes.
      toastKeyRef.current += 1;
      const key = toastKeyRef.current;
      setToasts((current) => [...current, { key, id }].slice(-TOAST_MAX));
      const timer = window.setTimeout(() => {
        setToasts((current) => current.filter((toast) => toast.key !== key));
      }, TOAST_MS);
      timersRef.current.push(timer);
    }
  }, []);

  useEffect(
    () => () => {
      for (const timer of timersRef.current) window.clearTimeout(timer);
      timersRef.current = [];
    },
    [],
  );

  // ── Clavier ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const set = (event: KeyboardEvent, down: boolean): void => {
      const key = event.key;
      if (key === 'ArrowLeft' || key === 'a' || key === 'q') keysRef.current.left = down;
      else if (key === 'ArrowRight' || key === 'd') keysRef.current.right = down;
      else if (key === ' ' || key === 'ArrowUp' || key === 'Enter') keysRef.current.fire = down;
      else return;
      // Les flèches et l'espace font défiler la page : pas pendant une partie.
      event.preventDefault();
      // Le clavier reprend la main sur le pointeur.
      inputRef.current = { ...inputRef.current, aimX: null };
    };

    const onDown = (event: KeyboardEvent): void => {
      // En pause, `Entrée` sert à valider le bouton du tableau, pas à relancer
      // une partie derrière lui.
      if (event.key === 'Enter' && !pausedRef.current && gameRef.current.phase !== 'playing') start();
      set(event, true);
    };
    const onUp = (event: KeyboardEvent): void => {
      set(event, false);
    };

    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [start]);

  /*
   * `Échap` ferme d'abord le tableau, pas le jeu.
   *
   * Le skin écoute la même touche sur `window`, en phase de bulle, pour sortir
   * du mini-jeu. Écouter ici en phase de **capture** passe avant lui : la
   * première pression referme le tableau, la seconde quitte la partie. Sans
   * cela, ouvrir les succès rendait `Échap` destructeur.
   */
  useEffect(() => {
    if (!panelOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setPanelOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [panelOpen]);

  // ── Boucle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.imageSmoothingEnabled = false;

    let frame = 0;
    let previous = performance.now();
    let published = { score: -1, lives: -1, wave: -1, phase: 'ready' as GameState['phase'] };

    const paint = (state: GameState): void => {
      context.fillStyle = '#05050c';
      context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

      // Fond : quelques bulles fixes, pour que le bac ne soit pas vide.
      context.fillStyle = '#101024';
      for (let index = 0; index < 18; index += 1) {
        const x = (index * 53) % GAME_WIDTH;
        const y = (index * 37) % GAME_HEIGHT;
        context.fillRect(x, y, 2, 2);
      }

      for (const foe of state.foes) {
        if (!foe.alive) continue;
        drawSprite(context, FOE, foe.x, foe.y, 1, ROW_PALETTES[foe.row] ?? SPRITE_PALETTE);
      }
      for (const bubble of state.bubbles) {
        drawSprite(context, BUBBLE, Math.round(bubble.x), Math.round(bubble.y), 1);
      }
      for (const hook of state.hooks) {
        drawSprite(context, HOOK, Math.round(hook.x), Math.round(hook.y), 1);
      }

      // Steve clignote pendant son invulnérabilité.
      const blinking = state.flashIn > 0 && Math.floor(state.flashIn / 100) % 2 === 0;
      if (!blinking) drawSprite(context, STEVE, Math.round(state.steveX), STEVE_LINE, 1);
    };

    const loop = (now: number): void => {
      const dt = now - previous;
      previous = now;

      /*
       * En pause, on ne fait rien avancer — et surtout on garde `previous` à
       * jour : sans ça, la reprise arriverait avec tout le temps passé dans le
       * tableau des succès d'un coup.
       */
      if (pausedRef.current) {
        frame = window.requestAnimationFrame(loop);
        return;
      }

      const keys = keysRef.current;
      inputRef.current = { ...inputRef.current, left: keys.left, right: keys.right, fire: keys.fire };
      const before = gameRef.current;
      const next = stepGame(before, inputRef.current, dt);
      gameRef.current = next;
      paint(next);

      const fresh = unlockedBy(before, next).filter((id) => !wonRef.current.has(id));
      if (fresh.length > 0) celebrate(fresh);

      if (
        next.score !== published.score ||
        next.lives !== published.lives ||
        next.wave !== published.wave ||
        next.phase !== published.phase
      ) {
        published = { score: next.score, lives: next.lives, wave: next.wave, phase: next.phase };
        setHud({ score: next.score, lives: next.lives, wave: next.wave, phase: next.phase });
      }
      frame = window.requestAnimationFrame(loop);
    };

    paint(gameRef.current);
    frame = window.requestAnimationFrame(loop);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [celebrate]);

  // ── Record ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (hud.phase !== 'over' || hud.score <= best) return;
    setBest(hud.score);
    writeString(BEST_KEY, String(Math.min(BEST_MAX, hud.score)));
  }, [hud.phase, hud.score, best]);

  /** Vise avec le pointeur : la position du doigt ou de la souris pilote Steve. */
  const aim = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * GAME_WIDTH;
    inputRef.current = { ...inputRef.current, aimX: x };
  };

  const playing = hud.phase === 'playing';

  return (
    <div className={styles.gameLayer} role="dialog" aria-modal="true" aria-label={t(S.pixel.game.label, lang)}>
      <div className={styles.gameFrame}>
        <div className={styles.gameHud}>
          <span className={styles.gameScore}>{t(S.pixel.game.score, lang, { n: hud.score })}</span>
          <span className={styles.gameWave}>{t(S.pixel.game.wave, lang, { n: hud.wave })}</span>
          <span className={styles.gameLives} aria-label={`${String(hud.lives)}`}>
            {'♥'.repeat(hud.lives)}
            <span className={styles.gameLivesLost}>{'♥'.repeat(START_LIVES - hud.lives)}</span>
          </span>
        </div>

        <div className={styles.gameScreen}>
          <canvas
            ref={canvasRef}
            className={styles.gameCanvas}
            width={GAME_WIDTH}
            height={GAME_HEIGHT}
            aria-hidden="true"
            onPointerDown={(event) => {
              event.preventDefault();
              aim(event);
              inputRef.current = { ...inputRef.current, fire: true };
              keysRef.current.fire = true;
              if (!playing) start();
            }}
            onPointerMove={(event) => {
              if (event.pressure > 0 || event.pointerType === 'mouse') aim(event);
            }}
            onPointerUp={() => {
              keysRef.current.fire = false;
            }}
            onPointerCancel={() => {
              keysRef.current.fire = false;
            }}
          />

          {panelOpen && (
            <AwardsPanel
              won={won}
              lang={lang}
              onClose={() => {
                setPanelOpen(false);
              }}
            />
          )}

          {!playing && !panelOpen && (
            <div className={styles.gameOverlay}>
              <p className={styles.gameTitle}>
                {hud.phase === 'over' ? t(S.pixel.game.over, lang) : t(S.pixel.game.title, lang)}
              </p>
              <p className={styles.gameLine}>
                {hud.phase === 'over' ? t(S.pixel.game.over_line, lang) : t(S.pixel.game.tagline, lang)}
              </p>
              {hud.phase === 'over' && hud.score >= best && hud.score > 0 && (
                <p className={styles.gameBest}>{t(S.pixel.game.new_best, lang)}</p>
              )}
              <button type="button" className={styles.gameButton} onClick={start}>
                {hud.phase === 'over' ? t(S.pixel.game.again, lang) : t(S.pixel.game.start, lang)}
              </button>
              <p className={styles.gameKeys}>
                {fine ? t(S.pixel.game.keys, lang) : t(S.pixel.game.touch, lang)}
              </p>
            </div>
          )}
        </div>

        <div className={styles.gameFooter}>
          <span className={styles.gameBestLine}>{t(S.pixel.game.best, lang, { n: best })}</span>
          <button
            type="button"
            className={styles.gameButton}
            aria-expanded={panelOpen}
            onClick={() => {
              setPanelOpen((open) => !open);
            }}
          >
            {t(S.pixel.awards.counter, lang, { n: won.length, total: ACHIEVEMENTS.length })}
          </button>
          <button type="button" className={styles.gameButton} onClick={onQuit}>
            {t(S.pixel.game.quit, lang)}
          </button>
          <span className={styles.gameKeys}>{t(S.pixel.game.escape, lang)}</span>
        </div>
      </div>

      {/*
        Hors du cadre, volontairement.
        Posées dans l'écran de jeu, les notifications masquaient le coin où
        arrivent les hameçons. Elles vivent donc au niveau du calque, dans la
        marge sombre : jamais au-dessus du canvas, et toujours en bas à droite
        comme sur Steam.
      */}
      <AwardToasts toasts={toasts} lang={lang} />
    </div>
  );
}
